import { motion } from "framer-motion";
import {
  Buildings,
  Package,
  MapPinLine,
  Truck,
  Warehouse,
  ArrowRight,
} from "@phosphor-icons/react";
import { SERVICES } from "@/data/company";

const ICONS = { Buildings, Package, MapPinLine, Truck, Warehouse };

export default function Services() {
  return (
    <section
      id="services"
      className="py-20 lg:py-32 bg-[#F8FAFC] border-y-2 border-slate-900"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-[2px] bg-[#EA580C]" />
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-slate-500">
                What we move
              </span>
            </div>
            <h2 className="font-display font-black text-slate-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Services engineered
              <br />
              for the Indian road.
            </h2>
          </div>
          <p className="text-slate-600 max-w-md font-medium leading-relaxed">
            From single-truck government dispatches to multi-vehicle
            industrial rollouts, every consignment is treated with the same
            operational discipline.
          </p>
        </div>

        {/* Technical grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-2 border-slate-900 bg-white">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon] || Truck;
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.06 }}
                data-testid={`service-card-${s.key}`}
                className="group relative p-8 lg:p-10 border-slate-900 border-r border-b last:border-r-0 md:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r lg:[&:nth-child(3n)]:border-r-0 transition-brutal hover:bg-[#0F172A] hover:text-white cursor-pointer"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-14 h-14 bg-[#EA580C] flex items-center justify-center group-hover:bg-[#F59E0B] transition-brutal">
                    <Icon size={28} weight="duotone" className="text-white" />
                  </div>
                  <span className="font-display font-black text-slate-200 group-hover:text-white/20 text-3xl leading-none transition-brutal">
                    0{idx + 1}
                  </span>
                </div>
                <h3 className="font-display font-black text-2xl tracking-tight text-slate-900 group-hover:text-white transition-brutal">
                  {s.title}
                </h3>
                <p className="mt-3 text-slate-600 group-hover:text-white/70 text-sm leading-relaxed font-medium transition-brutal">
                  {s.description}
                </p>
                <div className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] group-hover:text-[#F59E0B] transition-brutal">
                  Enquire
                  <ArrowRight
                    size={16}
                    weight="bold"
                    className="transition-transform group-hover:translate-x-1"
                  />
                </div>
              </motion.div>
            );
          })}

          {/* Filler CTA card so the last row stays 3-wide on desktop */}
          <motion.a
            href="#quote"
            data-testid="services-cta-quote"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.36 }}
            className="hidden lg:flex flex-col justify-between p-8 lg:p-10 bg-[#EA580C] text-white transition-brutal hover:bg-[#C2410C]"
          >
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-white/80 mb-3">
                Need a quote?
              </div>
              <div className="font-display font-black text-3xl leading-tight">
                Tell us your route &amp; payload. We&rsquo;ll respond fast.
              </div>
            </div>
            <div className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
              Request Now <ArrowRight size={18} weight="bold" />
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
